'use client';
import React, {HTMLAttributes, ReactNode, useEffect, useState} from 'react';

import Image from 'next/image';
import parser from 'html-react-parser';
import {getTranslation} from '@/lib/utils';
import {Locale} from '../../../i18n.config';
import {parseOptions} from '@/utils/parseOptions';
import {UnderlinedButton} from '../UnderlinedButton';

interface IFAQSectionProps extends HTMLAttributes<HTMLDivElement> {
  lang: Locale;
  itemsData: GuideItem[];
}

export const FAQSection: React.FC<IFAQSectionProps> = ({itemsData, lang}) => {
  const [currentItem, setCurrentItem] = useState(0);

  useEffect(() => {
    setCurrentItem(itemsData[0].id);
  }, [itemsData]);

  const tabs = itemsData.map((tab) => (
    <div key={tab.id} className={`bg-white relative flex-col w-full flex`}>
      <UnderlinedButton
        className="pt-5 text-gray-900"
        handleClick={() => setCurrentItem(tab.id)}
        isUnerlined={tab.id === currentItem}
        title={getTranslation(tab.guideItemTranslation || [], lang).title}
      />
    </div>
  ));

  const items = itemsData.map((item) => {
    const description =
      getTranslation(item.guideItemTranslation || [], lang).description || '';

    const formattedDescription = description.replace(
      'https://www.villatravellers.com',
      '',
    );

    return (
      <div
        key={item.id}
        className={`h-full bg-white relative flex-row w-full desktop:hidden ${
          currentItem === item.id && '!flex'
        }`}>
        <div className="w-0 desktop:w-1/4 desktop:pr-4 shrink-0 desktop:h-auto h-full"></div>

        <div className="w-1/4 h-auto desktop:block hidden shrink-0 min-h-[100vh] z-[8]">
          <div className="w-full h-full desktop:h-full flex shrink-0 relative">
            <Image
              src={`https://villatravellers-prod.s3.amazonaws.com/thumbs/guide_faq_slim/${item.image?.path}${item.image?.filename}`}
              alt={getTranslation(item.guideItemTranslation || [], lang).title}
              fill
              className="invisible object-top desktop:visible object-cover z-10 absolute h-full"
              loading="eager"
              priority
              sizes="(min-width: 1200px) 100vw, 0vw"
            />
          </div>
        </div>

        <div className="w-full desktop:w-2/5 px-0 dekstop:px-4 h-fit">
          <div className="py-[50px] px-[30px]">
            <h1 className="font-title text-4xl mb-2 font-bold">
              {getTranslation(item.guideItemTranslation || [], lang).title}
            </h1>
            <div className="font-text text-base h-fit visible text-gray-900 overflow-auto space-y-4">
              {parser(formattedDescription, parseOptions)}
            </div>
          </div>
        </div>
      </div>
    );
  });

  return (
    <section className="px-4 tablet:px-10 bg-white flex flex-col min-h-[100vh] h-full desktop:flex-row w-full desktop:px-20 !mt-0 relative">
      <div className="w-0 hidden desktop:block overflow-y-hidden desktop:w-[calc(25%-80px)] absolute h-full z-[8]">
        {tabs}
      </div>
      {items}
    </section>
  );
};
